import { isEqual, memoize } from 'lodash';
import { IndexedStorage, NativeStorage, QueryableStorage, SyncModule, SyncService } from '@weavix/sync';
import { PartitionEventStorage } from '@weavix/sync/src/storage/partition-event-storage';
import { map, of, distinctUntilChanged } from 'rxjs';

function indexMessage(cm: any) {
    return `${cm.channelId}|${cm.date instanceof Date ? cm.date.toISOString() : cm.date}`; // <channel id>|<date>
}

function indexMessagePartition(index: string) {
    const pipe = index.indexOf('|');
    return index.substring(0, pipe + 11); // Just by day
}

function indexId(value: any) {
    return value.id;
}

function indexIdPartition(index: string) {
    return index.substring(index.length - 1);
}

type CreateStorage = new (name: string) => NativeStorage;
let storage: CreateStorage = function () { throw new Error('Call configureStorage first'); } as any;
export function configureStorage(creator: CreateStorage) {
    storage = creator;
}

export interface StorageOptions {
    name: string;
    topics?: string | string[];
    readonly?: boolean;
}

// Forcing storage to refresh on version change
// when adding/changing the mobx model, add the storage name to the object and increment with -v#
// native sync ignores this; bump SyncModuleDefinition.schemaVersion for the same model change
const STORAGE_VERSIONS = {
    'users': 'users-v2',
    'beacons': 'beacons-v2',
    'facilities': 'facilities-v2',
    'companies': 'companies-v2',
    'geofences': 'geofences-v2',
};

export const getStorageVersionName = (name: string) => {
    if (STORAGE_VERSIONS[name]) return STORAGE_VERSIONS[name];
    else return name;
};

// eslint-disable-next-line complexity
export const getAccountStorage = memoize(<T extends { id: string }>(options: string | StorageOptions) => {
    if (typeof options === 'string') options = { name: options };
    const { name, topics, readonly } = options;

    let userTopic = false;
    let accountTopic = false;
    let removedAccountTopic = false;
    let staticTopic = false;
    let sync = false;
    let priority = false;
    let simulcast = false;
    let waltTopic = false;

    switch (name) {
        case 'statuses':
        case 'notifications':
        case 'settings':
        case 'meetings':
            userTopic = true;
            break;
        case 'channels':
        case 'channel-preferences':
        case 'channel-messages':
            userTopic = true;
            simulcast = true;
            break;
        case 'features':
            userTopic = false;
            accountTopic = false;
            staticTopic = true;
            waltTopic = true;
            break;
        case 'companies':
            accountTopic = true;
            staticTopic = true;
            waltTopic = true;
            break;
        case 'users':
            userTopic = true;
            accountTopic = true;
            removedAccountTopic = true;
            break;
        case 'connections':
            userTopic = true;
            break;
        case 'beacons':
        case 'facilities':
        case 'floor-plans':
        case 'geofences':
        case 'network-locations':
        case 'site-surveys-observations':
        case 'site-surveys-readings':
            waltTopic = true;
            /* FALLTHROUGH */
        default:
            sync = true;
            accountTopic = true;
            break;
    }
    let indexFn;
    let indexPart;
    switch (name) {
        case 'channel-messages':
            indexFn = indexMessage;
            indexPart = indexMessagePartition;
            break;
        case 'people':
        case 'users':
            priority = true;
        /** FALLTHROUGH */
        case 'facility-people':
        case 'site-surveys-observations':
            // partition by last character of ID to distribute writes
            indexFn = indexId;
            indexPart = indexIdPartition;
            break;
    }

    const lazy = !priority && accountTopic;
    const newStorage = new storage(getStorageVersionName(name));
    const indexedStorage = new IndexedStorage<T>(newStorage, indexFn, indexPart, readonly);
    class Module extends SyncModule<T> {
        storage = indexedStorage;
        // specific topics do no store events since the sync is incomplete
        eventStorage = !!topics || readonly ? null : new PartitionEventStorage(newStorage, indexedStorage);
        userId: string;

        constructor() {
            super(lazy, simulcast, staticTopic);
        }

        watchPartitions() {
            if (topics) return of(Array.isArray(topics) ? topics : [topics]);

            return SyncService.authentication$.pipe(
                distinctUntilChanged((prev, curr) => {
                    return prev?.user?.id === curr?.user?.id &&
                        isEqual(prev?.user?.accounts.map(x => x.id).sort(), curr?.user?.accounts.map(x => x.id).sort()) &&
                        isEqual(prev?.user?.removedAccountIds?.sort(), curr?.user?.removedAccountIds?.sort()) &&
                        prev?.walt?.accountId === curr?.walt?.accountId;
                }),
                map(auth => {
                    const user = auth?.user;
                    const walt = auth?.walt;
                    if (!user && !walt) return null;
                    const partitions = [];
                    if (user) {
                        this.userId = user.id;
                        if (staticTopic) partitions.push('root');
                        if (userTopic) partitions.push(user.id);
                        if (accountTopic) partitions.push(...user.accounts.map(x => x.id));
                        if (removedAccountTopic && user.removedAccountIds) partitions.push(...user.removedAccountIds);
                    } else if (walt && waltTopic) {
                        if (user) console.warn('walt data should not be mixed with user data! Unexpected behavior may occur!');
                        if (staticTopic) partitions.push('root');
                        if (accountTopic) partitions.push(walt.accountId);
                    }
                    return partitions;
                }),
            );
        }

        getName() {
            return name;
        }

        clearEventStorageCache() {
            this.eventStorage?.clearCache();
        }

        async flushEvents() {
            await this.eventStorage?.flush();
            await this.storage.flush();
        }

        getTopic(topic: string) {
            if (topics) return topic;
            return topic === 'root' ? `account/${name}`
                : topic === this.userId ? `user/${topic}/${name}`
                : `account/${topic}${sync ? '/sync' : ''}/${name}`;
        }
    }
    class Storage extends QueryableStorage<T> {
        static inst: Storage;
        static Instance = () => {
            if (!this.inst) this.inst = new Storage(new Module());
            this.inst.start();
            return this.inst;
        };
        private started = false;

        constructor(public readonly mod: Module) {
            super(name, mod.storage);
        }

        start() {
            if (!this.started) {
                this.started = true;
                this.mod.start();
            }
        }

        close() {
            if (this.started) {
                this.started = false;
                this.mod.close(false);
                this.mod.stop();
            }
        }
    }
    return Storage;
}, storageKey);

/**
 * Memo key for {@link getAccountStorage}: a name string and options with the same name, topics and readonly flag build the same storage, so they share one instance.
 * Two instances of one store would run two syncs over the same saved data and miss each other's writes.
 */
function storageKey(options: string | StorageOptions) {
    const { name, topics, readonly }: StorageOptions = typeof options === 'string' ? { name: options } : options;
    return JSON.stringify([name, topics ?? null, !!readonly]);
}
